import { ALIGN_CODE, DEFAULT_FONT, DEFAULT_HEADLINE, DISPLAY_CODE, LanguageCode, UNIT_CODE, VISIBILITY_CODE, WIDGET_TYPE } from '../utils';
import sanitize from 'sanitize-html';

export default class ParametersWidget {
  private language: string;
  private displayValue: string;
  private align: string;
  private backgroundColor: string;
  private fontFamily: string;
  private fontSize: number;
  private textColor: string;
  private headline: any;
  private headlineWeight: number;
  private donorNameWeight: number;
  private valueWeight: number;
  private identifier: string;
  private visibility: string;
  private unit: string;

  constructor(location: string, widgetType: string) {
    let parameters: any = new URLSearchParams(location);
    const newParams = new URLSearchParams();
    for (let [key, value] of parameters.entries()) {
      newParams.set(key.toLowerCase(), value);
    }

    parameters = newParams;

    this.language = this.getLanguage(parameters.get('language')?.toLowerCase() || LanguageCode.DE.toLowerCase());
    this.displayValue = this.getDisplayValue(parameters.get('displayvalue')?.toLowerCase() || DISPLAY_CODE.SQM);
    this.align = this.getAlign(parameters.get('align')?.toLowerCase() || ALIGN_CODE.CENTER);
    this.backgroundColor = parameters.get('backgroundcolor') || 'transparent';
    this.fontFamily = parameters.get('fontfamily') || DEFAULT_FONT;
    this.fontSize = isNaN(parseInt(parameters.get('fontsize') || '20')) ? 20 : parseInt(parameters.get('fontsize') || '20');
    this.textColor = parameters.get('fontcolor') || '#000000';
    this.headline = this.getHeadline(
      parameters.get('headline')?.toLowerCase() ||
        (widgetType === WIDGET_TYPE.DONATION
          ? DEFAULT_HEADLINE.DONATION[(this.language.toUpperCase() as LanguageCode) || LanguageCode.DE]
          : DEFAULT_HEADLINE.TOTAL[(this.language.toUpperCase() as LanguageCode) || LanguageCode.DE]),
      widgetType,
    );
    this.headlineWeight = isNaN(parseInt(parameters.get('headlineweight') || '400')) ? 400 : parseInt(parameters.get('headlineweight') || '400');
    this.donorNameWeight = isNaN(parseInt(parameters.get('donornameweight') || '700')) ? 700 : parseInt(parameters.get('donornameweight') || '700');
    this.valueWeight = isNaN(parseInt(parameters.get('valueweight') || '700')) ? 700 : parseInt(parameters.get('valueweight') || '700');
    this.identifier = parameters.get('identifier') || '';
    this.visibility = this.getVisibility(parameters.get('visibility')?.toLowerCase()) || '';
    this.unit = this.getUnit(parameters.get('unit')?.toLowerCase() || UNIT_CODE.KG);
  }

  public getLanguage(value: string): string {
    if (Object.values(LanguageCode).includes(value.toUpperCase() as LanguageCode)) {
      return value as LanguageCode;
    }

    return LanguageCode.DE.toLowerCase();
  }

  public getUnit(value: string): string {
    if (Object.values(UNIT_CODE).includes(value.toLowerCase() as UNIT_CODE)) {
      return value as UNIT_CODE;
    }

    return UNIT_CODE.KG;
  }

  public getDisplayValue(value: string): string {
    if (Object.values(DISPLAY_CODE).includes(value.toLowerCase() as DISPLAY_CODE)) {
      return value as DISPLAY_CODE;
    }

    return DISPLAY_CODE.SQM;
  }

  public getAlign(value: string): string {
    if (Object.values(ALIGN_CODE).includes(value.toLowerCase() as ALIGN_CODE)) {
      return value as ALIGN_CODE;
    }

    return ALIGN_CODE.CENTER;
  }

  public getVisibility(value: string): string {
    if (Object.values(VISIBILITY_CODE).includes(value as VISIBILITY_CODE)) {
      return value as VISIBILITY_CODE;
    }

    return VISIBILITY_CODE.ALL;
  }

  public getBackgroundColor() {
    return this.backgroundColor || 'transparent';
  }

  public getFontFamily(): string {
    return this.fontFamily || DEFAULT_FONT;
  }

  public getFontSize(): number {
    return this.fontSize || 20;
  }

  public getTextColor(): string {
    return this.textColor || '#000000';
  }

  public getHeadline(value: string, widgetType: string): boolean | string {
    if (value === 'true') {
      return widgetType === WIDGET_TYPE.DONATION
        ? DEFAULT_HEADLINE.DONATION[(this.language.toUpperCase() as LanguageCode) || LanguageCode.DE]
        : DEFAULT_HEADLINE.TOTAL[(this.language.toUpperCase() as LanguageCode) || LanguageCode.DE];
    }

    if (value === 'false') {
      return false;
    }

    if (typeof value === 'string') {
      return sanitize(
        value ||
          (widgetType === WIDGET_TYPE.DONATION
            ? DEFAULT_HEADLINE.DONATION[(this.language.toUpperCase() as LanguageCode) || LanguageCode.DE]
            : DEFAULT_HEADLINE.TOTAL[(this.language.toUpperCase() as LanguageCode) || LanguageCode.DE]),
      );
    }

    return false;
  }

  public getHeadlineWeight(): number {
    return this.headlineWeight || 400;
  }

  public getDonorNameWeight(): number {
    return this.donorNameWeight || 700;
  }

  public getValueWeight(): number {
    return this.valueWeight || 700;
  }

  public getIdentifier(): string {
    return this.identifier || '';
  }

  public getLanguageValue(): string {
    return this.language || LanguageCode.DE.toLowerCase();
  }

  public getAlignValue(): string {
    return this.align || ALIGN_CODE.CENTER;
  }

  public getVisibilityValue(): string {
    return this.visibility || VISIBILITY_CODE.ALL;
  }

  public getUnitValue(): string {
    return this.unit || UNIT_CODE.KG;
  }

  public getDisplayValueValue(): string {
    return this.displayValue || DISPLAY_CODE.SQM;
  }

  public getHeadlineValue(): boolean | string {
    return this.headline;
  }
}
